import React, { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';
import { Users, Briefcase, Clock, AlertCircle, TrendingUp } from 'lucide-react';

type DemographicData = {
  label: string;
  count: number;
}[];

interface DemographicsCardProps {
  roles: DemographicData;
  industries: DemographicData;
  experience: DemographicData;
}

export default function DemographicsCard({ roles, industries, experience }: DemographicsCardProps) {
  
  // Memoizing data formatting for performance
  const formatData = useMemo(() => (data: DemographicData) => {
    if (!data?.length) return [];
    const total = data.reduce((sum, item) => sum + item.count, 0);
    return data
      .sort((a, b) => b.count - a.count) // Highest first for better readability
      .map(item => ({
        ...item,
        percentage: parseFloat(((item.count / total) * 100).toFixed(1))
      }));
  }, []);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload?.length) {
      return (
        <div className="bg-slate-900/90 backdrop-blur-md border border-slate-700 p-3 rounded-xl shadow-2xl ring-1 ring-white/10">
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">{label}</p>
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full" style={{ backgroundColor: payload[0].fill }} />
            <p className="text-lg font-bold text-white">
              {payload[0].value}% 
              <span className="text-sm font-normal text-slate-400 ml-2">({payload[0].payload.count} users)</span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center h-[300px] border-2 border-dashed border-slate-800 rounded-xl bg-slate-900/40">
      <AlertCircle className="h-10 w-10 text-slate-600 mb-3 animate-pulse" />
      <p className="text-slate-400 font-medium">No demographic data found</p>
    </div>
  );

  const renderChart = (data: DemographicData, color: string) => (
    <div className="h-[350px] w-full mt-4 transition-all duration-500 ease-in-out">
      {data?.length ? (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={formatData(data)} 
            layout="vertical" 
            margin={{ left: 10, right: 30, top: 10, bottom: 10 }}
          >
            <defs>
              <linearGradient id={`colorGradient-${color}`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="5%" stopColor={color} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={color} stopOpacity={0.3}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#1e293b" />
            <XAxis 
              type="number" 
              hide // Hiding X axis for a cleaner "Progress Bar" look
            />
            <YAxis 
              type="category" 
              dataKey="label" 
              width={100} 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#94a3b8', fontSize: 12, fontWeight: 500 }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
            <Bar 
              dataKey="percentage" 
              radius={[0, 6, 6, 0]} 
              barSize={24}
              animationDuration={1500}
            >
              {formatData(data).map((entry, index) => (
                <Cell key={`cell-${index}`} fill={`url(#colorGradient-${color})`} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <EmptyState />
      )}
    </div>
  );

  return (
    <Card className="relative overflow-hidden border-slate-800 bg-slate-950 shadow-2xl">
      {/* Decorative Background Glow */}
      <div className="absolute -top-24 -right-24 h-64 w-64 bg-cyan-500/10 blur-[100px] rounded-full" />
      
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="text-2xl font-bold tracking-tight text-white flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-cyan-400" />
              User Insights
            </CardTitle>
            <CardDescription className="text-slate-400 text-sm sm:text-base">
              Detailed breakdown of your audience segments
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <Tabs defaultValue="roles" className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-slate-900 border border-slate-800 p-1">
            <TabsTrigger value="roles" className="rounded-md transition-all data-[state=active]:bg-slate-800 data-[state=active]:text-cyan-400 data-[state=active]:shadow-sm">
              <Users className="h-4 w-4 mr-2 hidden sm:inline" />
              Roles
            </TabsTrigger>
            <TabsTrigger value="industries" className="rounded-md transition-all data-[state=active]:bg-slate-800 data-[state=active]:text-indigo-400">
              <Briefcase className="h-4 w-4 mr-2 hidden sm:inline" />
              Industries
            </TabsTrigger>
            <TabsTrigger value="experience" className="rounded-md transition-all data-[state=active]:bg-slate-800 data-[state=active]:text-orange-400">
              <Clock className="h-4 w-4 mr-2 hidden sm:inline" />
              Exp.
            </TabsTrigger>
          </TabsList>

          <div className="mt-6">
            <TabsContent value="roles" className="outline-none focus:ring-0">
              {renderChart(roles, '#22d3ee')}
            </TabsContent>
            <TabsContent value="industries" className="outline-none">
              {renderChart(industries, '#818cf8')}
            </TabsContent>
            <TabsContent value="experience" className="outline-none">
              {renderChart(experience, '#fb923c')}
            </TabsContent>
          </div>
        </Tabs>
      </CardContent>
    </Card>
  );
}
